import { useCallback } from 'react'
import { Input, InputGroup, Text, VStack } from '@chakra-ui/react'

interface ModalTimeInputProps {
  label?: string
  name: string
  placeholder?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  isInvalid?: boolean
  isDisabled?: boolean
  step?: number
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max)
}

const ModalTimeInput: React.FC<ModalTimeInputProps> = ({
  label,
  name,
  placeholder,
  value,
  onChange,
  required,
  isInvalid,
  isDisabled,
}) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    if (raw === '') {
      const synth = { ...e, target: { ...e.target, name, value: '' } } as React.ChangeEvent<HTMLInputElement>
      onChange(synth)
      return
    }

    let digits = raw.replace(/[^0-9]/g, '').slice(0, 4)

    // ── Hours (digits 0-1) ──
    if (digits.length >= 1) {
      const h0 = parseInt(digits[0], 10)
      if (h0 > 2) {
        // Single digit > 2 can't start a valid hour → auto-prefix with 0
        digits = '0' + digits
      }
    }

    let hh = ''
    let mm = ''

    if (digits.length >= 2) {
      let h = parseInt(digits.slice(0, 2), 10)
      h = clamp(h, 0, 23)
      hh = String(h).padStart(2, '0')
    } else if (digits.length === 1) {
      hh = digits[0]
    }

    // ── Minutes (digits 2-3) ──
    if (digits.length >= 3) {
      const m0 = parseInt(digits[2], 10)
      if (m0 > 5) {
        // Single digit > 5 can't start valid minutes → auto-prefix with 0
        digits = digits.slice(0, 2) + '0' + digits.slice(2)
      }
    }

    if (digits.length >= 4) {
      let m = parseInt(digits.slice(2, 4), 10)
      m = clamp(m, 0, 59)
      mm = String(m).padStart(2, '0')
    } else if (digits.length === 3) {
      mm = digits[2]
    }

    let result = hh
    if (mm !== '' || digits.length > 2) result += ':' + mm

    const synth = { ...e, target: { ...e.target, name, value: result } } as React.ChangeEvent<HTMLInputElement>
    onChange(synth)
  }, [name, onChange])

  return (
    <VStack align="left" spacing="4px" width="100%">
      {label && (
        <Text textStyle="l-xs" color={required ? 'solid.700' : 'solid.500'} fontWeight={required ? '600' : undefined}>
          {label}
          {required && <Text as="span" color="primary.100"> *</Text>}
        </Text>
      )}
      <InputGroup>
        <Input
          type="text"
          inputMode="numeric"
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder ?? 'HH:MM'}
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          maxLength={5}
        />
      </InputGroup>
    </VStack>
  )
}

export default ModalTimeInput
